import { MouseEvent, useEffect, useState } from 'react';
import axios from 'axios';

import Loading from '../../../shared/Loading';
import { Modal, SpeechBubble } from '../../../shared';

import { usePackMembership } from '../../../hooks/packs';

import { filterBannersByStatus } from '../../../banners/utils';
import { filterCarouselsByStatus } from '../../../carousels/utils';
import { filterTabsByStatus } from '../../../shopTabs/utils';
import {
    CAROUSELS_BASE_PATH,
    SHOP_TABS_BASE_PATH,
} from '../../../utils/pathsConstants';
import {
    getPackDisplayName,
    handleError,
    isProductInactive,
} from '../../../utils/utils';
import { filterPacksByStatus } from '../../utils';

import { Banner } from '../../../types/bannerTypes';
import { Carousel } from '../../../types/carouselTypes';
import { Pack } from '../../../types/packTypes';
import { Tab } from '../../../types/tabTypes';

import styles from './PackMembership.module.scss';

type PackMembershipProps = {
    packId: string;
};

type PackMembershipType = 'bundle' | 'carousel' | 'tab' | 'banner';

const getRemoveFromBundleUrl = (packId: string, childPackId: string) => {
    return `/api/store/v2/pack/${packId}/child/${childPackId}`;
};

const getRemoveFromCarouselUrl = (itemId: number) => {
    return `/api/${CAROUSELS_BASE_PATH}/item/${itemId}`;
};

const getRemoveFromBannerUrl = (bannerId: number, packId: string) => {
    return `/api/banners/v2/${bannerId}/pack/${packId}`;
};

const getRemoveFromTabUrl = (tabId: number, packId: string) => {
    return `/api/${SHOP_TABS_BASE_PATH}/${tabId}/${packId}`;
};

enum MembershipFilter {
    ShowAll = 'all',
    ShowActive = 'active',
    ShowInactive = 'inactive',
}

export function PackMembership(props: PackMembershipProps) {
    const { packId } = props;

    const [filterMemberships, setFilterMemberships] =
        useState<MembershipFilter>(MembershipFilter.ShowAll);

    const {
        membership: { packs, carousels, tabs, banners },
        fetch: fetchPackMembership,
        isLoading,
        error,
    } = usePackMembership(packId);

    useEffect(() => {
        fetchPackMembership();
    }, []);

    const createOnRemoveFromProductClick = (
        name: string,
        type: PackMembershipType,
        url: string,
    ) => {
        return async (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            const isConfirmed = await Modal.confirm({
                heading: 'Remove pack',
                text: `Really remove this pack from "${name}" ${type}?`,
                okLabel: 'Yes do it!',
            });

            if (!isConfirmed) {
                return;
            }

            try {
                await axios.delete(url);
                await fetchPackMembership();
            } catch (e) {
                handleError(`Error deleting pack from the ${type}.`, e);
            }
        };
    };

    const createOnRemoveFromCarouselClick = (
        carousel: Carousel,
        itemId: number,
    ) =>
        createOnRemoveFromProductClick(
            carousel.carouselName,
            'carousel',
            getRemoveFromCarouselUrl(itemId),
        );

    const createOnRemoveFromPackClick = (pack: Pack) =>
        createOnRemoveFromProductClick(
            getPackDisplayName(pack),
            'bundle',
            getRemoveFromBundleUrl(pack.packId, packId),
        );

    const createOnRemoveFromTabClick = (tab: Tab) =>
        createOnRemoveFromProductClick(
            tab.name,
            'tab',
            getRemoveFromTabUrl(tab.id, packId),
        );

    const createOnRemoveFromBannerClick = (banner: Banner) =>
        createOnRemoveFromProductClick(
            banner.name,
            'banner',
            getRemoveFromBannerUrl(banner.id, packId),
        );

    const setMembershipFilter = (filter: MembershipFilter) => {
        setFilterMemberships(filter);
    };

    const getMembershipPacks = () => {
        return filterPacksByStatus(packs, filterMemberships)
            .sort((p1, p2) =>
                getPackDisplayName(p2).localeCompare(getPackDisplayName(p1)),
            )
            .sort((p) => (isProductInactive(p) ? 1 : -1));
    };

    const getMembershipCarousels = () => {
        return filterCarouselsByStatus(carousels, filterMemberships)
            .sort((c1, c2) => c1.carouselName.localeCompare(c2.carouselName))
            .sort((c) => (c.active ? -1 : 1));
    };

    const getMembershipTabs = () => {
        return filterTabsByStatus(tabs, filterMemberships)
            .sort((t1, t2) => t1.name.localeCompare(t2.name))
            .sort((t) => (t.active ? -1 : 1));
    };

    const getMembershipBanners = () => {
        return filterBannersByStatus(banners, filterMemberships)
            .sort((b1, b2) => b1.name.localeCompare(b2.name))
            .sort((b) => (isProductInactive(b) ? 1 : -1));
    };

    if (isLoading) {
        return <Loading style={{ textAlign: 'center' }} />;
    }

    if (error) {
        handleError('Error fetching pack membership data', error);
    }

    if (
        packs.length === 0 &&
        carousels.length === 0 &&
        tabs.length === 0 &&
        banners.length === 0
    ) {
        return (
            <SpeechBubble style={{ justifyContent: 'left', margin: '10px 0' }}>
                This pack is not member of any bundles, carousels, tabs or
                banners.
            </SpeechBubble>
        );
    }

    return (
        <>
            <fieldset>
                <legend>Status</legend>
                <input
                    checked={filterMemberships === MembershipFilter.ShowAll}
                    id={`showAll`}
                    value={MembershipFilter.ShowAll}
                    type="radio"
                    onChange={() =>
                        setMembershipFilter(MembershipFilter.ShowAll)
                    }
                />
                <label htmlFor="showAll">All</label>
                <input
                    checked={filterMemberships === MembershipFilter.ShowActive}
                    id={`showActive`}
                    value={MembershipFilter.ShowActive}
                    type="radio"
                    onChange={() =>
                        setMembershipFilter(MembershipFilter.ShowActive)
                    }
                />
                <label htmlFor="showActive">Active</label>
                <input
                    checked={
                        filterMemberships === MembershipFilter.ShowInactive
                    }
                    id={`showInactive`}
                    value={MembershipFilter.ShowInactive}
                    type="radio"
                    onChange={() =>
                        setMembershipFilter(MembershipFilter.ShowInactive)
                    }
                />
                <label htmlFor="showInactive">Inactive</label>
            </fieldset>
            {packs.length > 0 && (
                <div className={styles.packUsageSection}>
                    <h2>Bundles</h2>
                    <ul>
                        {getMembershipPacks().map((p) => (
                            <li key={p.packId}>
                                <div>
                                    <a
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        href={`/pack/${p.packId}`}
                                    >
                                        {getPackDisplayName(p)}
                                    </a>{' '}
                                    {isProductInactive(p) && (
                                        <span className="inactive">
                                            (Inactive)
                                        </span>
                                    )}
                                </div>

                                <button
                                    style={{ margin: '0 3px' }}
                                    className="small danger"
                                    onClick={createOnRemoveFromPackClick(p)}
                                >
                                    Remove from this bundle
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {carousels.length > 0 && (
                <div className={styles.packUsageSection}>
                    <h2>Carousels</h2>
                    <ul>
                        {getMembershipCarousels().map((c) => {
                            const [{ carouselItemId }] = c.items;

                            return (
                                <li key={c.carouselId}>
                                    <div>
                                        <a
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            href={`/carousel/${c.carouselId}`}
                                        >
                                            {c.carouselName}
                                        </a>{' '}
                                        {!c.active && (
                                            <span className="inactive">
                                                (Inactive)
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        style={{ margin: '0 3px' }}
                                        className="small danger"
                                        onClick={createOnRemoveFromCarouselClick(
                                            c,
                                            carouselItemId,
                                        )}
                                    >
                                        Remove from this carousel
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            {tabs.length > 0 && (
                <div className={styles.packUsageSection}>
                    <h2>Tabs</h2>
                    <ul>
                        {getMembershipTabs().map((t) => (
                            <li key={t.id}>
                                <div>
                                    <a
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        href={`/shoptab/${t.id}`}
                                    >
                                        {t.name}
                                    </a>
                                    {!t.active && (
                                        <span className="inactive">
                                            (Inactive)
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="small danger"
                                    onClick={createOnRemoveFromTabClick(t)}
                                >
                                    Remove from this tab
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {banners.length > 0 && (
                <div className={styles.packUsageSection}>
                    <h2>Banners</h2>
                    <ul>
                        {getMembershipBanners().map((b) => (
                            <li key={b.id}>
                                <div>
                                    <a
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        href={`/banners/${b.id}`}
                                    >
                                        {b.name}
                                    </a>
                                    {isProductInactive(b) && (
                                        <span className="inactive">
                                            (Inactive)
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="small danger"
                                    onClick={createOnRemoveFromBannerClick(b)}
                                >
                                    Remove from this banner
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
}

export default PackMembership;
